<template>
    <div class="new-gallery__container">
        <div class="new-gallery__img-area">
            <template v-if="allowImaginStudio && selectedType == '360'">
                <img class="img-responsive new-gallery__starburst" src="/images/configurator/stellantis-starburst.svg" alt="Stellantis Starburst">
                <img class="new-gallery__360-img img-responsive" ref="360" :src="currentImage" alt="Vehicle image" draggable="false" />
                <img v-for="(image, index) in allImages" :src="image" alt="" style="display: none" :key="'preload-gallery-' + index" />
                <div class="new-gallery__rotate-msg" v-if="showRotateMessage">Drag to rotate</div>
            </template>
            <template v-else-if="haveCMSGallery && selectedType == 'gallery'">
                <div class="swiper new-gallery__swiper" ref="gallery">
                    <div class="swiper-wrapper">
                        <figure class="swiper-slide new-gallery__figure" v-for="(galleryImage, index) in exteriorGallery" :key="'exterior-gallery-' + index" itemprop="associatedMedia" itemscope itemtype="http://schema.org/ImageObject">
                            <picture>
                                <source type="image/webp" :data-srcset="galleryImage.webp">
                                <img :src="galleryImage.jpg" class="img-responsive" itemprop="thumbnail" alt="">
                            </picture>
                        </figure>
                    </div>
                    <div v-show="cmsGalleryLength > 1" class="new-gallery__swiper-btn new-gallery__swiper-prev" ref="swiper-prev"></div>
                    <div v-show="cmsGalleryLength > 1" class="new-gallery__swiper-btn new-gallery__swiper-next" ref="swiper-next"></div>
                </div>
            </template>
            <template v-else-if="!haveCMSGallery && selectedType == 'gallery'">
                <img class="img-responsive new-gallery__starburst" src="/images/configurator/stellantis-starburst.svg" alt="Stellantis Starburst">
                <div class="swiper new-gallery__swiper" ref="gallery" v-if="flagToResetGalleryHTML">
                    <div class="swiper-wrapper">
                        <figure class="swiper-slide new-gallery__figure" v-for="(galleryImage, index) in imaginStudioGalleryImages" :key="'gallery-' + index" itemprop="associatedMedia" itemscope itemtype="http://schema.org/ImageObject">
                            <a ref="gallery-images" target="_blank" :href="imaginStudioGalleryImagesForPhotoswipe[index].src" @click.prevent="$photoswipe.open($refs['gallery-images'], index, imaginStudioGalleryImagesForPhotoswipe)" :data-index="index" itemprop="contentUrl">
                                <picture>
                                    <source type="image/webp" :data-srcset="galleryImage + '&fileType=webp'">
                                    <img :data-src="galleryImage + '&fileType=png'" class="img-responsive swiper-lazy" itemprop="thumbnail" alt="">
                                </picture>
                            </a>
                            <div class="new-gallery__preloader">
                                <div class="swiper-lazy-preloader"></div>
                            </div>
                        </figure>
                    </div>
                    <div class="new-gallery__swiper-btn new-gallery__swiper-prev" ref="swiper-prev"></div>
                    <div class="new-gallery__swiper-btn new-gallery__swiper-next" ref="swiper-next"></div>
                </div>
            </template>
            <template v-else-if="selectedType == 'interior'">
                <div class="swiper" ref="interior-gallery">
                    <div class="swiper-wrapper">
                        <figure class="swiper-slide new-gallery__figure" v-for="(galleryImage, index) in interiorGallery" :key="'interior-gallery-' + index" itemprop="associatedMedia" itemscope itemtype="http://schema.org/ImageObject">
                            <picture>
                                <source type="image/webp" :data-srcset="galleryImage.webp">
                                <img :src="galleryImage.jpg" class="img-responsive" itemprop="thumbnail" alt="">
                            </picture>
                        </figure>
                    </div>
                    <div class="new-gallery__swiper-btn new-gallery__swiper-prev" ref="swiper-prev"></div>
                    <div class="new-gallery__swiper-btn new-gallery__swiper-next" ref="swiper-next"></div>
                </div>
            </template>
        </div>
        <div class="new-gallery__paint-options" ref="colorPicker" v-if="selectedType == '360' || (!haveCMSGallery && selectedType == 'gallery')">
            <div class="new-gallery__paint-name">{{ selectedPaintName }}</div>
            <div id="color-picker" class="new-gallery__color-picker">
                <div id="prev-button" class="new-gallery__nav-button prev-button" @click="prevPaintOptions" v-if="currentStartIndex > 0" >
                    <svg class="new-gallery__svg" xmlns="http://www.w3.org/2000/svg" viewbox="0 0 45 45" height="45px" width="45px">
                        <circle cx="22.5" cy="22.5" r="22.5" fill="#FFF" />
                        <path fill="#0093D6" fill-rule="nonzero" d="M22.5 0a22.5 22.5 0 1 1 0 45 22.5 22.5 0 0 1 0-45Zm0 1.5a21 21 0 1 0 0 42 21 21 0 0 0 0-42Z"/>
                        <path fill="#0093D6" fill-rule="nonzero" d="M24.4 13.5a.7.7 0 1 1 1.2 1L19 22.4l6.6 7.9c.2.3.2.7 0 1h-.1c-.3.3-.8.3-1 0l-7-8.4a.8.8 0 0 1 0-1l7-8.4Z"/>
                    </svg>
                </div>
                <button type="button" @click="setSelectedPaint(paint)" v-for="paint in currentPaintOptions" :key="'paint-' + paint.id" class="new-gallery__paint-btn" :class="{'new-gallery__paint-btn--active': selectedPaint.id == paint.id}">
                    <div class="new-gallery__paint-btn-inner" :style="'background-color: ' + paint.primary_hex"></div>
                </button>
                <div id="next-button" class="new-gallery__nav-button next-button" @click="nextPaintOptions" v-if="shouldShowNextButton">
                    <svg class="new-gallery__svg" xmlns="http://www.w3.org/2000/svg" viewbox="0 0 45 45" height="45px" width="45px">
                        <circle cx="22.5" cy="22.5" r="22.5" fill="#FFF" />
                        <path fill="#0093D6" fill-rule="nonzero" d="M22.5 0a22.5 22.5 0 1 1 0 45 22.5 22.5 0 0 1 0-45Zm0 1.5a21 21 0 1 0 0 42 21 21 0 0 0 0-42Z"/>
                        <path fill="#0093D6" fill-rule="nonzero" d="M21.6 13.5a.7.7 0 1 0-1.2 1l6.6 7.9-6.6 7.9a1 1 0 0 0 0 1h.1c.3.3.8.3 1 0l7-8.4c.3-.3.3-.7 0-1l-7-8.4Z"/>
                    </svg>
                </div>
            </div>
        </div>
        <div class="new-gallery__btn-area">
            <button class="btn btn--primary-inverted" :class="{'btn-primary': selectedType == 'gallery'}" type="button" @click="changeGallery('gallery')">Gallery</button>
            <button class="btn btn--primary-inverted" :class="{'btn-primary': selectedType == 'interior'}" type="button" @click="changeGallery('interior')" v-if="haveInterior">Interior</button>
            <button v-if="allowImaginStudio" class="btn btn--primary-inverted" :class="{'btn-primary': selectedType == '360'}" type="button" @click="changeGallery('360')">360&deg; spin</button>
            <a href="#full-spec" type="button" class="btn btn--primary-inverted">Specification</a>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import VuePhotoSwipe from '../../../core/components/Photoswipe';
import { mapState } from 'vuex';
import Hammer from 'hammerjs';
import Swiper, {Lazy, Navigation} from 'swiper';

Vue.use(VuePhotoSwipe);

export default {
    data() {
        return {
            loading: true,
            showRotateMessage: true,
            selectedType: null,
            angle: 204,
            startDragAngle: 0,
            currentStartIndex: 0,
            visibleItems: 5,
            swiper: null,
            flagToResetGalleryHTML: true,
        };
    },
    props: {
        preSelectedPaintCapId: {
            type: Number,
            required: false,
            default: 0,
        },
    },
    methods: {
        changeGallery(type, startingIndex = null) {
            if (this.swiper) {
                this.swiper.destroy(true);
            }
            this.selectedType = type;

            if (this.selectedType == '360') {
                this.$nextTick(function() {
                    const hammertime = new Hammer(this.$refs['360']);
                    this.showRotateMessage = true;

                    hammertime.on('panstart panend', (ev) => {
                        this.startDragAngle = this.angle;
                    });

                    hammertime.on('panleft panright', (ev) => {
                        let angleToChangeBy = Math.round((32/360) * ev.deltaX);
                        if (angleToChangeBy > 32) {
                            angleToChangeBy = (angleToChangeBy % 32) * 1;
                        } else if (angleToChangeBy < -32) {
                            angleToChangeBy = (Math.abs(angleToChangeBy) % 32) * -1;
                        }

                        const newAngle = this.startDragAngle + angleToChangeBy;
                        if (newAngle > 231) {
                            this.angle = (newAngle - 231) + 200;
                            return;
                        }
                        if (newAngle < 200) {
                            this.angle = 231 - (200 - newAngle);
                            return;
                        }
                        this.angle = newAngle;
                        this.showRotateMessage = false;
                    });
                });
                return;
            }
            if (this.selectedType == 'gallery') {
                this.$nextTick(function() {
                    const config = {
                        modules: [Lazy, Navigation],
                        grabCursor: true,
                        slidesPerView: 1,
                        spaceBetween: 10,
                        loop: (this.cmsGalleryLength > 1),
                        navigation: {
                            prevEl: this.$refs['swiper-prev'],
                            nextEl: this.$refs['swiper-next'],
                        },
                        lazy: {
                            enabled: true,
                            checkInView: true,
                            preloaderClass: 'new-gallery__preloader',
                        }
                    };
                    if (startingIndex) {
                        config.initialSlide = startingIndex;
                    }
                    this.swiper = new Swiper(this.$refs['gallery'], config);
                });
                return;
            }
            if (this.selectedType == 'interior') {
                this.$nextTick(function() {
                    this.swiper = new Swiper(this.$refs['interior-gallery'], {
                        modules: [Lazy, Navigation],
                        grabCursor: true,
                        slidesPerView: 1,
                        spaceBetween: 10,
                        preloadImages: false,
                        // loop: true,
                        allowSlidePrev: true,
                        allowSlideNext: true,
                        navigation: {
                            prevEl: this.$refs['swiper-prev'],
                            nextEl: this.$refs['swiper-next'],
                        },
                    });
                });
                return;
            }
        },
        setSelectedPaint(paint) {
            if (this.selectedType == 'gallery' && (typeof this.selectedPaint.id == 'undefined' || paint.id != this.selectedPaint.id)) {
                this.flagToResetGalleryHTML = false;
                this.$store.commit('NewVehicleData/setSelectedPaint', paint);
                let currentSlide = null;
                if (this.swiper) {
                    currentSlide = this.swiper.realIndex;
                }
                this.$nextTick(function() {
                    this.flagToResetGalleryHTML = true;
                    this.changeGallery('gallery', currentSlide);
                })
                return;
            }
            this.$store.commit('NewVehicleData/setSelectedPaint', paint);
        },
        updateVisibleItems() {
            this.$nextTick(() => {
                if (typeof this.$refs['colorPicker'] == 'undefined') {
                    return;
                }
                const containerWidth = this.$refs.colorPicker.clientWidth;
                const paintBtnElement = this.$refs.colorPicker.querySelector(
                    ".new-gallery__paint-btn"
                )
                const paintBtnWidth = paintBtnElement.offsetWidth;
                // Account for gap between colours
                const paintBtnTotalWidth = paintBtnWidth + 30;
                const maxButtons = Math.floor(containerWidth / paintBtnTotalWidth);
                this.visibleItems = maxButtons;
            });
        },
        prevPaintOptions() {
            if (this.currentStartIndex > 0) {
                this.currentStartIndex -= this.visibleItems;
                this.currentStartIndex = Math.max(0, this.currentStartIndex);
            }
        },
        nextPaintOptions() {
            if (this.currentStartIndex + this.visibleItems < this.paintOptions.length ) {
                this.currentStartIndex += this.visibleItems;
                // Adjust index if we're close to the end
                if (this.currentStartIndex +this.visibleItems > this.paintOptions.length) {
                    this.currentStartIndex = this.paintOptions.length - this.visibleItems;
                }
            }
        },
    },
    mounted() {
        if (this.preSelectedPaintCapId) {
            const paint = this.currentPaintOptions.find(paint => paint.cap_paint_id == this.preSelectedPaintCapId);

            if (paint) {
                this.$store.commit('NewVehicleData/setSelectedPaint', paint);
            }
        }

        this.changeGallery('gallery');
        this.updateVisibleItems();

        window.addEventListener("resize", this.updateVisibleItems);
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.updateVisibleItems);
    },
    computed: {
        ...mapState('NewVehicleData', ['paintOptions', 'gallery', 'selectedPaint', 'vehicle']),
        allowImaginStudio() {
            if (typeof this.vehicle['new_vehicles_trim'] == 'undefined') {
                return true;
            }
            if (typeof this.vehicle.new_vehicles_trim['disable_imagin_studio'] == 'undefined') {
                return true;
            }
            return this.vehicle.new_vehicles_trim.disable_imagin_studio == 0;
        },
        exteriorGallery() {
            const gallery = [];
            this.gallery.forEach(image => {
                if (image.exterior == 'Y') {
                    gallery.push({
                        jpg: image.exterior_filename_jpg,
                        webp: image.exterior_filename_webp,
                    });
                }
            });
            return gallery;
        },
        interiorGallery() {
            const gallery = [];
            this.gallery.forEach(image => {
                if (image.interior == 'Y') {
                    gallery.push({
                        jpg: image.interior_filename_jpg,
                        webp: image.interior_filename_webp,
                    });
                }
            });
            return gallery;
        },
        cmsGalleryLength() {
            return this.exteriorGallery.length;
        },
        haveCMSGallery() {
            return this.cmsGalleryLength > 0;
        },
        interiorLength() {
            return this.interiorGallery.length;
        },
        haveInterior() {
            return this.interiorLength > 0;
        },
        currentPaintOptions() {
            return this.paintOptions.slice(
                this.currentStartIndex,
                this.currentStartIndex + this.visibleItems
            );
        },
        shouldShowNextButton() {
            const remainingItems = this.paintOptions.length - this.currentStartIndex;
            // if there is only one more item show that instead of next button
            if(this.visibleItems + 1 === remainingItems ){
                this.visibleItems += 1;
            }else{
                return remainingItems > this.visibleItems;
            }
            return false
        },
        selectedPaintUrlIdentifier() {
            return this.selectedPaint.url_identifier;
        },
        selectedPaintName() {
            return this.selectedPaint.name;
        },
        imaginStudioGalleryImagesForPhotoswipe() {
            return [
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=01&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=05&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=09&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=13&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=17&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=21&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=22&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=23&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=25&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=27&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=28&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=29&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625},
                {src:'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=2600&angle=33&fileType=png&' + this.selectedPaintUrlIdentifier, w:2600, h:1625}
            ]
        },
        imaginStudioGalleryImages() {
            return [
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=01&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=05&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=09&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=13&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=17&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=21&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=22&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=23&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=25&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=27&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=28&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=29&' + this.selectedPaintUrlIdentifier,
                'https://cdn.imagin.studio/getImage?customer=robinsandday&zoomType=fullscreen&steering=right&width=800&angle=33&' + this.selectedPaintUrlIdentifier
            ];
        },
        currentImage() {
            return 'https://cdn.imagin.studio/getImage?customer=robinsandday&steering=right&width=800&angle=' + this.angle + '&' + this.selectedPaintUrlIdentifier;
        },
        allImages() {
            const images = [];
            for (let index = 200; index < 232; index++) {
                images.push('https://cdn.imagin.studio/getImage?customer=robinsandday&steering=right&width=800&angle=' + index + '&' + this.selectedPaintUrlIdentifier)
            }
            return images;
        },
    },
};
</script>

<style lang="scss">

</style>
